import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, Stack, TextField, Typography,
} from "@mui/material";
import api from "../../common/api";
import { roundToX } from "../../common/common";
import { setShims, loadCaptureScans, circlesToTsv } from "../../reducer/RawSurfaceSlice";
import { computeMotorRight } from "./shaftRanges";
import "../cards/ShimPanel.css";

/**
 * "Save Shim Values" for the Raw Surface tab: review the description, shaft ranges and foot
 * positions, then persist as a Scan.
 *
 * Shaft ranges and foot positions are set on the Shaft Ranges card (drag on a chart, or type
 * directly there) - this dialog only ever displays them, it doesn't edit them, so what's shown
 * here can never disagree with what actually produced the shim numbers being saved. Calculating
 * a preview also doesn't happen here - the rail's own "Calculate Shim Values" button
 * (RawSurfaceSaveButton.js) computes it directly via applyRawSurfaceFilters, without opening this
 * dialog. This dialog just shows whatever is already in rawSurface.shims (if anything) and posts
 * to the same scan/parse endpoint with preview: false to persist it.
 */
export default function RawSurfaceSaveDialog({ open, onClose }) {
    const rawSurface = useSelector((state) => state.rawSurface);
    const scans = useSelector((state) => state.scans);
    const auth = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    // Fixed to the AU the loaded raw capture actually belongs to (set in Raw Surface's own
    // toolbar) - not editable here, since this dialog only ever makes sense for that AU's data;
    // picking a different one would apply the wrong calibration to it.
    const auid = rawSurface.selectedAuId;
    const selectedAu = scans.au.find((au) => au.uniqueId === auid);

    const [description, setDescription] = useState("");
    // Lives in Redux (rawSurface.shims), not local state, so it stays visible in the Shaft
    // Ranges card after this dialog closes.
    const shims = rawSurface.shims;
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const busy = saving;

    const keptCircles = useMemo(
        () => rawSurface.circles.filter((c) => c.kept && c.pointCount > 0),
        [rawSurface.circles]
    );

    // Both shafts, fully specified, before Save is allowed. A shim compares the machine shaft
    // against the motor shaft, so a missing bound cannot produce a meaningful answer - and the
    // API would silently substitute its configured default rather than refuse, which is the wrong
    // answer this prevents. Same check as RawSurfaceSettings.js's rangesAndFeetSet.
    const rangesEntered = rawSurface.machineRange != null && rawSurface.motorRange != null
        && rawSurface.distanceToFF != null && rawSurface.distanceFtoR != null;

    // Bounds of the kept data, shown so the operator can see whether the current ranges actually
    // contain data.
    const extent = useMemo(() => {
        if (keptCircles.length === 0) return null;
        return {
            min: keptCircles[0].x,
            max: keptCircles[keptCircles.length - 1].x,
        };
    }, [keptCircles]);

    const submit = async () => {
        setError(null);
        setSaving(true);

        try {
            const payload = {
                data: circlesToTsv(rawSurface.circles),
                description: description || `Raw surface ${rawSurface.fileName}`,
                auid,
                preview: false,

                // Which capture and which acceptance settings produced this TSV. Sent on preview
                // too, for simplicity - the server records them only when it actually persists a
                // Scan, so a preview leaves no trace.
                captureId: rawSurface.captureId,
                circleFixEnabled: rawSurface.circleFixEnabled,
                circleFixLimit: rawSurface.circleFixLimit,
                angleEnabled: rawSurface.angleEnabled,
                angleLimit: rawSurface.angleLimit,
                pointEnabled: rawSurface.pointEnabled,
                pointLimit: rawSurface.pointLimit,
                sigmaEnabled: rawSurface.sigmaEnabled,
                maxSigma: rawSurface.maxSigma,

                // Surface Correction / Data Filter panel settings - whatever the user was just
                // previewing with, so Save persists a scan reflecting the same numbers, not
                // silently falling back to the server's configured defaults.
                correctY: rawSurface.correctY,
                correctZ: rawSurface.correctZ,
                nrsEnabled: rawSurface.nrsEnabled,
                nrsIterations: rawSurface.nrsIterations,
                nrsMultiplier: rawSurface.nrsMultiplier,
                nrsKeepBestPercent: rawSurface.nrsKeepBestPercent,
                focusEnabled: rawSurface.focusEnabled,
                focusIterations: rawSurface.focusIterations,
                focusEliminatePercent: rawSurface.focusEliminatePercent,
                focusKeepBestPercent: rawSurface.focusKeepBestPercent,
            };

            // Read straight from Redux, not local text state - this dialog has no editable copy
            // of ranges/foot positions any more, so there's nothing to keep in sync. rangesEntered
            // already requires all of these before Save is enabled.
            payload.machinestart = rawSurface.machineRange.start;
            payload.machinestop = rawSurface.machineRange.stop;
            payload.motorstart = rawSurface.motorRange.start;
            payload.motorstop = rawSurface.motorRange.stop;
            payload.distancetoff = rawSurface.distanceToFF;
            payload.distanceftor = rawSurface.distanceFtoR;

            // Which shaft is physically on the right - without this, the server silently defaults
            // to `true` regardless of where the ranges actually fall (see shaftRanges.js's
            // computeMotorRight doc comment).
            payload.motorright = computeMotorRight(rawSurface.machineRange, rawSurface.motorRange);

            const result = await new api().request("scan/parse", auth.token, "post", payload);

            // The save returns the same shim values it persisted, so the rail can go on showing
            // them - now correctly labelled as saved rather than as an unsaved preview.
            dispatch(setShims({
                shims: result.shims, saved: true, filterTrace: result.filterTrace, summary: result.summary,
            }));
            // Refresh the saved-scan list so the just-saved scan is there when the comparison
            // dialog is opened, and so "Compare Saved Scans" enables after the first save.
            dispatch(loadCaptureScans({ captureId: rawSurface.captureId }));
            onClose(true);
        } catch (err) {
            setError(err?.response?.data?.detail ?? err?.message ?? "Request failed");
        } finally {
            setSaving(false);
        }
    };

    // ShimValues is four flat doubles in microns. Same conversion, rounding, and Front/Rear ×
    // Vertical/Horizontal grid as ShimsCard.js (Scan Analysis), reusing its own ShimPanel.css
    // classes, so a shim previewed here reads exactly like it does everywhere else in the app.
    const MICRON_TO_INCH = 0.00003937;
    const shimInches = (microns) =>
        typeof microns === "number" ? roundToX(microns * MICRON_TO_INCH, 3).toFixed(3) : "-";

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Shim Values from Raw Surface</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Alert severity="info">
                        Using <strong>{keptCircles.length.toLocaleString()}</strong> kept circles of{" "}
                        {rawSurface.circles.length.toLocaleString()}. Dropped circles are excluded, which
                        is what the thresholds mean.
                        {extent && (
                            <> Data spans x = {extent.min.toLocaleString()} to {extent.max.toLocaleString()} µm.</>
                        )}
                    </Alert>

                    <Box>
                        <Typography variant="caption" color="text.secondary">Alignment Unit</Typography>
                        <Typography variant="body2">{selectedAu ? selectedAu.name : auid}</Typography>
                    </Box>

                    {/* Read-only: shaft ranges and foot positions are set on the Shaft Ranges
                        card (drag on a chart, or type there), never here - so what's shown always
                        matches what actually produced the shim numbers below. */}
                    <Stack direction="row" spacing={4}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Machine</Typography>
                            <Typography variant="body2">
                                {rawSurface.machineRange
                                    ? `${Math.round(rawSurface.machineRange.start).toLocaleString()} – ${Math.round(rawSurface.machineRange.stop).toLocaleString()} µm`
                                    : "—"}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Motor</Typography>
                            <Typography variant="body2">
                                {rawSurface.motorRange
                                    ? `${Math.round(rawSurface.motorRange.start).toLocaleString()} – ${Math.round(rawSurface.motorRange.stop).toLocaleString()} µm`
                                    : "—"}
                            </Typography>
                        </Box>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        Foot positions: {rawSurface.distanceToFF ?? "—"} µm to front foot,{" "}
                        {rawSurface.distanceFtoR ?? "—"} µm front-to-rear.
                    </Typography>
                    {!rangesEntered && (
                        <Alert severity="warning" sx={{ py: 0.25 }}>
                            Shaft ranges and both foot positions must be set on the Shaft Ranges
                            card before shim values can be saved.
                        </Alert>
                    )}

                    <TextField
                        label="Description" size="small" fullWidth
                        value={description} onChange={(e) => setDescription(e.target.value)}
                    />

                    {busy && (
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">Saving scan…</Typography>
                        </Stack>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}

                    {shims && (
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                                Calculated shims (not saved)
                            </Typography>
                            <Grid container direction="row" sx={{ marginLeft: "auto", marginRight: "auto" }}>
                                <Box className="shim-column shim-label" />
                                <Box className="shim-column shim-caption">Front</Box>
                                <Box className="shim-column shim-caption">Rear</Box>
                            </Grid>
                            <Grid container direction="row">
                                <Box className="shim-column shim-label">Vertical</Box>
                                <Box className="shim-column shim-value">{shimInches(shims.frontVertical)}</Box>
                                <Box className="shim-column shim-value">{shimInches(shims.rearVertical)}</Box>
                            </Grid>
                            <Grid container direction="row">
                                <Box className="shim-column shim-label">Horizontal</Box>
                                <Box className="shim-column shim-value">{shimInches(shims.frontHorizontal)}</Box>
                                <Box className="shim-column shim-value">{shimInches(shims.rearHorizontal)}</Box>
                            </Grid>
                            {/* Same caption as the Shaft Ranges card: these numbers are only
                                meaningful alongside the calibration that produced them. */}
                            {rawSurface.appliedCalibration && (
                                <Typography
                                    variant="caption"
                                    display="block"
                                    color={rawSurface.appliedCalibration.predatesCapture
                                        ? "warning.main" : "text.secondary"}
                                    sx={{ mt: 1 }}
                                >
                                    Calibration{" "}
                                    {new Date(rawSurface.appliedCalibration.dateTime).toLocaleString()}
                                    {rawSurface.appliedCalibration.scanCount
                                        ? ` · ${rawSurface.appliedCalibration.scanCount} scans`
                                        : ""}
                                    {rawSurface.appliedCalibration.predatesCapture
                                        ? " · taken after this capture"
                                        : ""}
                                </Typography>
                            )}
                        </Box>
                    )}

                </Stack>
            </DialogContent>
            <DialogActions>
                {/* "Close" once a preview exists - nothing left to cancel, closing just dismisses
                    the dialog and leaves the (unsaved) preview showing in the Shaft Ranges card. */}
                <Button color="inherit" onClick={() => onClose(false)} disabled={busy}>
                    {shims ? "Close" : "Cancel"}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => submit()}
                    disabled={busy || keptCircles.length === 0 || !rangesEntered}
                >
                    Save as Scan
                </Button>
            </DialogActions>
        </Dialog>
    );
}
